// src/api/apiClient.js
//
// Cliente HTTP centralizado del web HolaVeci. Un fetch wrapper con:
//   · baseUrl configurable
//   · Bearer token automático desde localStorage
//   · 401 handling → limpia sesión y redirige a /login
//   · Errores tipados con mensajeUsuario

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5246';

const TOKEN_KEY = 'hv_web_jwt';

export const tokenStorage = {
  get:    () => localStorage.getItem(TOKEN_KEY),
  set:    (t) => localStorage.setItem(TOKEN_KEY, t),
  clear:  () => localStorage.removeItem(TOKEN_KEY),
  exists: () => !!localStorage.getItem(TOKEN_KEY),
};

let onSesionExpirada = null;
export const setOnSesionExpirada = (fn) => { onSesionExpirada = fn; };

// Expuesto para llamadas que NO pasan por api.* (fetch crudo, como
// verificacionApi.js, que sube FormData con multipart y no encaja bien
// en el wrapper JSON de abajo). Sin esto, un 401 fuera de api.* nunca
// dispara la limpieza de sesión/redirect — el usuario se queda viendo
// un error genérico sin saber que en realidad necesita reloguearse.
export const manejarNoAutorizado = () => {
  tokenStorage.clear();
  onSesionExpirada?.();
};

export class ApiError extends Error {
  constructor({ statusCode, message, mensajeUsuario }) {
    super(message);
    this.statusCode      = statusCode;
    this.mensajeUsuario  = mensajeUsuario;
  }
}

const mensajeUsuarioDefault = (status, backend) => {
  if (backend) return backend;
  switch (status) {
    case 400: return 'Datos inválidos. Revisa e inténtalo de nuevo.';
    case 401: return 'Tu sesión expiró. Vuelve a iniciar sesión.';
    case 403: return 'No tienes permiso para esta acción.';
    case 404: return 'No se encontró lo que buscas.';
    case 409: return 'Conflicto con la información actual.';
    case 500: return 'Error del servidor. Inténtalo en unos minutos.';
    default:  return 'Algo salió mal. Inténtalo de nuevo.';
  }
};

async function request(method, path, { body, query, skipAuth } = {}) {
  const url = new URL(API_BASE_URL + path);
  if (query) Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
  });

  const headers = { 'Content-Type': 'application/json' };
  if (!skipAuth) {
    const token = tokenStorage.get();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  let response;
  try {
    response = await fetch(url.toString(), {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new ApiError({
      statusCode: 0,
      message: err.message || 'network error',
      mensajeUsuario: 'No se pudo conectar al servidor. Revisa tu conexión e inténtalo de nuevo.',
    });
  }

  if (response.status === 401) {
    tokenStorage.clear();
    onSesionExpirada?.();
  }

  let data = null;
  const text = await response.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }

  if (!response.ok) {
    const backendMessage = data?.mensaje || data?.message || (typeof data === 'string' ? data : null);
    throw new ApiError({
      statusCode:     response.status,
      message:        backendMessage || `HTTP ${response.status}`,
      mensajeUsuario: mensajeUsuarioDefault(response.status, backendMessage),
    });
  }

  return data;
}

export const api = {
  get:    (path, opts)     => request('GET',    path, opts),
  post:   (path, body, opts) => request('POST',   path, { ...opts, body }),
  put:    (path, body, opts) => request('PUT',    path, { ...opts, body }),
  patch:  (path, body, opts) => request('PATCH',  path, { ...opts, body }),
  delete: (path, opts)     => request('DELETE', path, opts),
};
